import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, inject } from '@angular/core';
import { MaterialModule } from '../../../shared/material/material.module';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Payment } from '../../../core/generated';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PaymentControllerService, } from '../../../core/generated';
import { firstValueFrom } from 'rxjs';
import { PaymentCardComponent } from './payment-card/payment-card.component';
import { PaymentDetailsDialogComponent } from '../../../shared/dialogs/payment-details-dialog/payment-details-dialog.component';
import { PaymentCardSkeletonComponent } from '../shared/skeleton-loaders';

/**
 * UserPaymentsComponent (Refactored)
 * Displays list of payments with search, filters, and responsive grid
 * Requirements: 7.1, 7.2, 7.4, 7.5, 7.6, 12.2, 13.2, 13.3, 15.1, 15.2
 */
@Component({
  selector: 'app-user-payments',
  standalone: true,
  imports: [MaterialModule, CommonModule, FormsModule, PaymentCardComponent, PaymentCardSkeletonComponent],
  templateUrl: './user-payments.component.html',
  styleUrl: './user-payments.component.scss'
})
export class UserPaymentsComponent implements OnInit, OnChanges {
  @Input() payments: Payment[] = [];
  @Input() filterByPlanId: number | null = null;
  @Input() isLoading = false; // Requirement 15.1
  @Output() paymentSelected = new EventEmitter<Payment>();
  @Output() planFilterCleared = new EventEmitter<void>();

  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private paymentsService = inject(PaymentControllerService,);

  // Filter state (Requirements 7.2, 7.4)
  searchTerm = '';
  statusFilter: 'all' | 'pending' | 'completed' | 'overdue' = 'all';
  filteredPayments: Payment[] = [];
  
  // Skeleton loader count (Requirement 15.2)
  skeletonCount = 6;
  
  // Processing state for button spinners (Requirement 15.3)
  processingInstallmentId: number | null = null;

  ngOnInit(): void {
    this.applyFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['payments'] || changes['filterByPlanId']) {
      this.applyFilters();
    }
  }

  /**
   * Apply search and status filters
   * Requirements: 7.2, 7.4, 13.2
   */
  applyFilters(): void {
    let result = [...this.payments];

    // Filter by plan ID (Requirement 13.2)
    if (this.filterByPlanId) {
      result = result.filter(p => p.accessPlanId === this.filterByPlanId);
    }

    // Filter by search term (Requirement 7.2)
    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      result = result.filter(p => 
        p.id?.toString().includes(term) ||
        p.method?.toLowerCase().includes(term)
      );
    }

    // Filter by status (Requirement 7.4)
    if (this.statusFilter !== 'all') {
      result = result.filter(p => {
        switch (this.statusFilter) {
          case 'pending':
            return p.status === Payment.StatusEnum.PendingPayment;
          case 'completed':
            return p.status === Payment.StatusEnum.Paid;
          case 'overdue':
            return this.hasOverdueInstallments(p);
          default:
            return true;
        }
      });
    }

    this.filteredPayments = result;
  }

  /**
   * Check if payment has overdue installments
   * Requirement: 7.4
   */
  hasOverdueInstallments(payment: Payment): boolean {
    const today = new Date();
    return (payment.installments || []).some(i => {
      if (i.status !== 'PAID' && i.dueDate) {
        return new Date(i.dueDate) < today;
      }
      return false;
    });
  }

  /**
   * Clear plan filter
   * Requirement: 13.2
   */
  clearPlanFilter(): void {
    this.planFilterCleared.emit();
  }

  /**
   * Open payment details modal
   * Requirements: 5.1, 13.3, 13.4
   */
  openPaymentDetails(payment: Payment): void {
    const dialogRef = this.dialog.open(PaymentDetailsDialogComponent, {
      data: { payment },
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '90vh'
    });

    // Handle dialog close actions
    dialogRef.afterClosed().subscribe(result => {
      if (result?.action === 'viewPlan') {
        // Emit event to navigate to plan
        this.paymentSelected.emit(payment);
      } else if (result?.action === 'payInstallment') {
        // Handle installment payment
        this.handlePayInstallment(result.installmentId);
      }
    });
  }

  /**
   * Handle installment payment
   * Requirements: 11.1, 15.3
   */
  async handlePayInstallment(installmentId: number): Promise<void> {
    this.processingInstallmentId = installmentId;
    
    try {
      await firstValueFrom(this.paymentsService.processInstallmentPayment(installmentId));
      this.snackBar.open('Parcela paga com sucesso!', 'Fechar', { duration: 3000 });
    } catch (error) {
      console.error('Erro ao processar pagamento:', error);
      this.snackBar.open('Erro ao processar pagamento', 'Fechar', { duration: 5000 });
    } finally {
      this.processingInstallmentId = null;
    }
  }

  /**
   * Get empty state icon based on context
   * Requirements: 12.2, 12.3
   */
  getEmptyStateIcon(): string {
    if (this.payments.length === 0) {
      return 'receipt_long'; // No payments at all
    }
    if (this.filterByPlanId) {
      return 'filter_list_off'; // No payments for filtered plan
    }
    return 'search_off'; // No search results
  }

  /**
   * Get empty state title based on context
   * Requirements: 12.2, 12.3
   */
  getEmptyStateTitle(): string {
    if (this.payments.length === 0) {
      return 'Nenhum pagamento registrado';
    }
    if (this.filterByPlanId) {
      return 'Nenhum pagamento para este plano';
    }
    return 'Nenhum resultado encontrado';
  }

  /**
   * Get empty state message based on context
   * Requirements: 12.2, 12.3
   */
  getEmptyStateMessage(): string {
    if (this.payments.length === 0) {
      return 'Você ainda não possui pagamentos registrados. Os pagamentos aparecerão aqui quando você contratar um plano.';
    }
    if (this.filterByPlanId) {
      return `Não há pagamentos associados ao plano #${this.filterByPlanId}. Remova o filtro para ver todos os pagamentos.`;
    }
    if (this.searchTerm && this.statusFilter !== 'all') {
      return `Nenhum pagamento encontrado com o termo "${this.searchTerm}" e status "${this.getStatusFilterLabel()}".`;
    }
    if (this.searchTerm) {
      return `Nenhum pagamento encontrado com o termo "${this.searchTerm}". Tente usar palavras-chave diferentes.`;
    }
    if (this.statusFilter !== 'all') {
      return `Nenhum pagamento ${this.getStatusFilterLabel().toLowerCase()} encontrado.`;
    }
    return 'Nenhum pagamento encontrado com os filtros aplicados. Tente ajustar os filtros.';
  }

  /**
   * Get status filter label for display
   */
  private getStatusFilterLabel(): string {
    const labels: { [key: string]: string } = {
      'all': 'Todos',
      'pending': 'Pendente',
      'completed': 'Completo',
      'overdue': 'Atrasado'
    };
    return labels[this.statusFilter] || 'Todos';
  }

  /**
   * Check if empty state should show action button
   * Requirements: 12.4
   */
  shouldShowEmptyStateAction(): boolean {
    // Show action button when there are payments but filters don't match
    return this.payments.length > 0 && this.filteredPayments.length === 0;
  }

  /**
   * Get empty state action label
   * Requirements: 12.4
   */
  getEmptyStateActionLabel(): string {
    if (this.filterByPlanId) {
      return 'Remover Filtro de Plano';
    }
    return 'Limpar Filtros';
  }

  /**
   * Get empty state action icon
   * Requirements: 12.4
   */
  getEmptyStateActionIcon(): string {
    if (this.filterByPlanId) {
      return 'filter_list_off';
    }
    return 'clear_all';
  }

  /**
   * Handle empty state action (clear filters)
   * Requirements: 12.4
   */
  handleEmptyStateAction(): void {
    if (this.filterByPlanId) {
      this.clearPlanFilter();
    } else {
      this.searchTerm = '';
      this.statusFilter = 'all';
      this.applyFilters();
    }
  }

  /**
   * Get array for skeleton loaders
   * Requirement 15.2
   */
  getSkeletonArray(): number[] {
    return Array(this.skeletonCount).fill(0);
  }

  /**
   * Check if installment is being processed
   * Requirement 15.3
   */
  isProcessingInstallment(installmentId: number): boolean {
    return this.processingInstallmentId === installmentId;
  }
}